/**
 * Play Lottie once when scrolled into view (no loop, no autoplay on load).
 * Expects e.g.:
 * <lottie-player class="lottieplayer" src="…/file.json" speed="1"></lottie-player>
 *
 * src MUST be a publicly reachable JSON/lottie URL (no login wall).
 */
class LottieScrollAnimator {
  constructor(selector = 'lottie-player, dotlottie-player, .lottieplayer') {
    this.selector = selector;
    this.entries = [];
    this.observer = null;

    const players = Array.prototype.slice
      .call(document.querySelectorAll(selector))
      .filter((player) => !player.dataset.lottieScrollBound);

    if (!players.length) {
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      root: null,
      rootMargin: '0px 0px',
      threshold: 0.25,
    });

    players.forEach((player) => this.register(player));
  }

  register(player) {
    player.dataset.lottieScrollBound = '1';
    this.prepare(player);

    const observeEl =
      player.closest('.lc-lottie-animation__media, [data-lottie-animation], .lc-lottie-wrapper') ||
      player;

    const entry = {
      player,
      observeEl,
      shouldPlay: false,
      hasStarted: false,
      hasFinished: false,
    };
    this.entries.push(entry);

    this.bindComplete(entry);
    this.bindIntrinsicSize(entry);

    const observe = () => {
      this.observer.observe(observeEl);
      this.syncIntrinsicSize(entry);

      // Always start paused; only play once scrolled into view
      this.pause(entry);

      if (this.isInViewport(observeEl)) {
        this.play(entry);
      }
    };

    const tag = (player.tagName || '').toUpperCase();
    if (tag === 'LOTTIE-PLAYER' && window.customElements?.whenDefined) {
      customElements.whenDefined('lottie-player').then(observe).catch(observe);
      return;
    }

    if (tag === 'DOTLOTTIE-PLAYER' && window.customElements?.whenDefined) {
      customElements.whenDefined('dotlottie-player').then(observe).catch(observe);
      return;
    }

    observe();
  }

  prepare(player) {
    player.removeAttribute('loop');
    player.removeAttribute('autoplay');

    if ('loop' in player) {
      player.loop = false;
    }

    if ('autoplay' in player) {
      player.autoplay = false;
    }

    if (!player.hasAttribute('background')) {
      player.setAttribute('background', 'transparent');
    }

    // Freeze on first frame until scroll triggers play
    if (typeof player.stop === 'function') {
      try {
        player.stop();
      } catch (error) {
        // ignore
      }
    } else if (typeof player.pause === 'function') {
      try {
        player.pause();
      } catch (error) {
        // ignore
      }
    }

    // Size to animation aspect ratio (not a forced square)
    player.style.width = '100%';
    player.style.height = 'auto';
    player.style.maxWidth = '100%';
  }

  bindIntrinsicSize(entry) {
    const { player } = entry;
    if (typeof player.addEventListener !== 'function') {
      return;
    }

    const sync = () => this.syncIntrinsicSize(entry);
    player.addEventListener('ready', sync);
    player.addEventListener('load', sync);
    player.addEventListener('rendered', sync);
    window.addEventListener('resize', sync);
  }

  syncIntrinsicSize(entry) {
    const { player } = entry;
    const size = this.getAnimationSize(player);
    if (!size) {
      return;
    }

    const width = player.clientWidth || player.offsetWidth || 0;
    if (!width) {
      return;
    }

    const height = Math.round(width * (size.h / size.w));
    player.style.width = '100%';
    player.style.height = `${height}px`;

    const media = player.closest('.lc-lottie-animation__media');
    if (media) {
      media.style.aspectRatio = `${size.w} / ${size.h}`;
    }
  }

  getAnimationSize(player) {
    try {
      const lottie = typeof player.getLottie === 'function' ? player.getLottie() : null;
      const data = lottie?.animationData || lottie || null;
      const w = data?.w || player.animationData?.w;
      const h = data?.h || player.animationData?.h;
      if (w && h) {
        return { w: Number(w), h: Number(h) };
      }
    } catch (error) {
      // ignore
    }
    return null;
  }

  bindComplete(entry) {
    const { player } = entry;
    if (typeof player.addEventListener !== 'function') {
      return;
    }

    const onComplete = () => {
      entry.hasFinished = true;
      entry.shouldPlay = false;
    };

    player.addEventListener('complete', onComplete);
    player.addEventListener('finish', onComplete);
  }

  isInViewport(el) {
    if (!el?.getBoundingClientRect) {
      return false;
    }

    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > vh * 0.25 && rect.top < vh * 0.75 && rect.width > 0 && rect.height > 0;
  }

  handleIntersection(entries) {
    entries.forEach((ioEntry) => {
      const match = this.entries.find((item) => item.observeEl === ioEntry.target);
      if (!match) {
        return;
      }

      if (ioEntry.isIntersecting) {
        this.play(match);
        return;
      }

      // Leave finished animations on last frame; pause mid-play
      if (!match.hasFinished) {
        this.pause(match);
      }
    });
  }

  play(entry) {
    if (entry.hasFinished) {
      return;
    }

    entry.shouldPlay = true;
    entry.hasStarted = true;
    const { player } = entry;

    const tryPlay = () => {
      if (!entry.shouldPlay || entry.hasFinished) {
        return;
      }

      if (typeof player.play === 'function') {
        try {
          player.play();
        } catch (error) {
          // Player may not be ready yet
        }
      }
    };

    tryPlay();

    if (typeof player.addEventListener === 'function') {
      player.addEventListener('ready', tryPlay, { once: true });
      player.addEventListener('load', tryPlay, { once: true });
      player.addEventListener('rendered', tryPlay, { once: true });
      player.addEventListener('error', () => {
        console.warn(
          '[Lottie] Failed to load animation. Check that src is a public JSON URL (not behind login).',
          player.getAttribute('src')
        );
      });
    }

    window.setTimeout(tryPlay, 100);
    window.setTimeout(tryPlay, 500);
  }

  pause(entry) {
    entry.shouldPlay = false;
    const { player } = entry;

    if (typeof player.pause === 'function') {
      try {
        player.pause();
      } catch (error) {
        // ignore
      }
      return;
    }

    if (typeof player.stop === 'function' && !entry.hasStarted) {
      try {
        player.stop();
      } catch (error) {
        // ignore
      }
    }
  }
}

export default LottieScrollAnimator;
